import type { DecodeVisualState } from './decode-visual-state';

export interface MaterialPoint {
  x: number;
  y: number;
}

/** No solved flag can invent light on disconnected channels. */
export function visibleDecodeEnergy(
  visual: DecodeVisualState,
  segmentId: string,
  animatedEnergy: number | undefined,
): number {
  if (!visual.energizedSegmentIds.has(segmentId)) return 0;
  return Math.max(0, Math.min(1, Number.isFinite(animatedEnergy) ? animatedEnergy! : 1));
}

export function decodeCascadeSegments(visual: DecodeVisualState): string[] {
  return [...visual.energizedSegmentIds].sort(
    (left, right) =>
      (visual.segments[left]?.depth ?? 0) - (visual.segments[right]?.depth ?? 0) ||
      left.localeCompare(right),
  );
}

/** Bounded high-density raster, baked only when the board layout changes, never per frame. */
export function decodeMaterialBounds(points: readonly MaterialPoint[]) {
  const left = Math.floor(Math.min(...points.map((p) => p.x))) - 5;
  const top = Math.floor(Math.min(...points.map((p) => p.y))) - 5;
  const width = Math.ceil(Math.max(...points.map((p) => p.x))) - left + 5;
  const height = Math.ceil(Math.max(...points.map((p) => p.y))) - top + 5;
  return { left, top, width, height, resolution: Math.min(2, 1024 / Math.max(width, height)) };
}

export function bakeDecodeMaterial(
  points: readonly MaterialPoint[],
  image: CanvasImageSource,
  seed: number,
  obsidian: boolean,
): { canvas: HTMLCanvasElement; bounds: ReturnType<typeof decodeMaterialBounds> } {
  const bounds = decodeMaterialBounds(points);
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(bounds.width * bounds.resolution);
  canvas.height = Math.ceil(bounds.height * bounds.resolution);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Stone material canvas unavailable');
  context.scale(bounds.resolution, bounds.resolution);
  context.translate(-bounds.left, -bounds.top);
  const trace = () => {
    context.beginPath();
    points.forEach((p, i) => (i ? context.lineTo(p.x, p.y) : context.moveTo(p.x, p.y)));
    context.closePath();
  };
  context.save();
  trace();
  context.clip();
  // Sample a different portion per stone; always uniform image scaling, never stretched grain.
  const side = Math.max(bounds.width, bounds.height) * 1.3;
  context.drawImage(
    image,
    bounds.left - ((seed % 23) / 100) * side,
    bounds.top - (((seed >>> 6) % 23) / 100) * side,
    side,
    side,
  );
  const light = context.createLinearGradient(
    bounds.left,
    bounds.top,
    bounds.left + bounds.width,
    bounds.top + bounds.height,
  );
  light.addColorStop(0, obsidian ? 'rgba(172,195,184,.13)' : 'rgba(255,242,204,.12)');
  light.addColorStop(0.55, 'rgba(0,0,0,0)');
  light.addColorStop(1, 'rgba(4,10,10,.32)');
  context.fillStyle = light;
  context.fillRect(bounds.left, bounds.top, bounds.width, bounds.height);
  // Inset bevel cut through the same photographed mineral, with fixed upper-left lighting.
  trace();
  context.lineWidth = 6;
  context.strokeStyle = obsidian ? '#060d0d' : '#514c3e';
  context.stroke();
  context.translate(-1.4, -1.4);
  trace();
  context.lineWidth = 2.2;
  context.strokeStyle = obsidian ? 'rgba(162,182,168,.45)' : 'rgba(244,222,178,.85)';
  context.stroke();
  context.restore();
  return { canvas, bounds };
}
